import React, { CSSProperties } from 'react';
import styled, { css } from 'styled-components';
import { Variant } from '../../common/variant';
import * as FeatherIcon from 'react-feather';
import * as FaRegularIcon from '@fortawesome/free-regular-svg-icons';
import * as FaSolidIcon from '@fortawesome/free-solid-svg-icons';
import * as FaBrandIcon from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { config, IconProp } from '@fortawesome/fontawesome-svg-core';
import { Size } from '../../common/size';
import { useTestId } from '../../hooks/useTestId';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { IThemePalette } from '../../Themes/interfaces';

export const iconColours = (palette: IThemePalette) => ({
  icon: {
    [Variant.Info]: {
      text: palette.primary.text[100]
    },
    [Variant.Success]: {
      text: palette.alerts.success
    },
    [Variant.Error]: {
      text: palette.alerts.alert
    }
  },
  circularIcon: {
    [Variant.Info]: {
      text: palette.primary.text[100],
      background: palette.alerts.notification
    },
    [Variant.Success]: {
      text: palette.primary.text[100],
      background: palette.alerts.success
    },
    [Variant.Error]: {
      text: palette.neutral.white,
      background: palette.alerts.alert
    }
  }
});

// https://fontawesome.com/v5.15/how-to-use/on-the-web/other-topics/security
config.autoAddCss = false;

const FeatherIconsWithoutCircle = {
  AlertCircleWithoutCircle: FeatherIcon.AlertCircle,
  ArrowDownCircleWithoutCircle: FeatherIcon.ArrowDownCircle,
  ArrowLeftCircleWithoutCircle: FeatherIcon.ArrowLeftCircle,
  ArrowRightCircleWithoutCircle: FeatherIcon.ArrowRightCircle,
  ArrowUpCircleWithoutCircle: FeatherIcon.ArrowUpCircle,
  CheckCircleWithoutCircle: FeatherIcon.CheckCircle,
  DivideCircleWithoutCircle: FeatherIcon.DivideCircle,
  HelpCircleWithoutCircle: FeatherIcon.HelpCircle,
  MessageCircleWithoutCircle: FeatherIcon.MessageCircle,
  MinusCircleWithoutCircle: FeatherIcon.MinusCircle,
  PauseCircleWithoutCircle: FeatherIcon.PauseCircle,
  PlayCircleWithoutCircle: FeatherIcon.PlayCircle,
  PlusCircleWithoutCircle: FeatherIcon.PlusCircle,
  XCircleWithoutCircle: FeatherIcon.XCircle,
  InfoWithoutCircle: FeatherIcon.Info,
  ClockWithoutCircle: FeatherIcon.Clock,
  CompassWithoutCircle: FeatherIcon.Compass,
  CrosshairWithoutCircle: FeatherIcon.Crosshair,
  DiscWithoutCircle: FeatherIcon.Disc,
  FrownWithoutCircle: FeatherIcon.Frown,
  MehWithoutCircle: FeatherIcon.Meh,
  SlashWithoutCircle: FeatherIcon.Slash,
  SmileWithoutCircle: FeatherIcon.Smile
};

export const createIconNames = <T extends string | number | symbol>(type: any): Record<T, T> => {
  const names: Record<T, T> = {} as Record<T, T>;
  Object.keys(type).forEach((key) => {
    if (!['prefix', 'fab', 'fas', 'far'].includes(key)) {
      names[key as T] = key as T;
    }
  });

  return names;
};

const ExtendedFeatherIcons = { ...FeatherIcon, ...FeatherIconsWithoutCircle };

export type IconName = keyof typeof FeatherIcon | keyof typeof FeatherIconsWithoutCircle;

export const IconNames = createIconNames<IconName>(ExtendedFeatherIcons);
export type FaRegularIconName = keyof typeof FaRegularIcon;
export const FaRegularIconNames = createIconNames<FaRegularIconName>(FaRegularIcon);
export type FaSolidIconName = keyof typeof FaSolidIcon;
export const FaSolidIconNames = createIconNames<FaSolidIconName>(FaSolidIcon);
export type FaBrandIconName = keyof typeof FaBrandIcon;
export const FaBrandIconNames = createIconNames<FaBrandIconName>(FaBrandIcon);

export interface IIconProps extends React.HTMLAttributes<SVGElement>, Pick<CSSProperties, 'opacity'> {
  name?: IconName;
  farName?: FaRegularIconName;
  fasName?: FaSolidIconName;
  fabName?: FaBrandIconName;
  size?: Size;
  variant?: Variant;
  'data-testid'?: string;
}

const iconStyles: Record<Size, ReturnType<typeof css>> = {
  // TODO: update with extraSmall sizes
  [Size.ExtraSmall]: css`
    && {
      width: 0.875rem;
    }
    height: 0.875rem;
    padding: 0.313rem;
  `,
  // 24px 1.5rem
  [Size.Small]: css`
    && {
      width: 0.875rem;
    }
    height: 0.875rem;
    padding: 0.313rem;
  `,
  // 32px 2rem
  [Size.Medium]: css`
    && {
      width: 1.125rem;
    }
    height: 1.125rem;
    padding: 0.438rem;
  `,
  // 64px 4rem
  [Size.Large]: css`
    && {
      width: 1.375rem;
    }
    height: 1.375rem;
    padding: 1.313rem;
  `,
  // TODO: update with extraLarge sizes
  [Size.ExtraLarge]: css`
    && {
      width: 1.375rem;
    }
    height: 1.375rem;
    padding: 1.313rem;
  `
};

type ForwardRef = ((instance: unknown) => void) | React.MutableRefObject<unknown> | null;
const FontAwesomeIconWithRef: React.FC<FontAwesomeIconProps & { ref: ForwardRef }> = FontAwesomeIcon;

const IconBase = React.forwardRef<unknown, IIconProps>(function IconBase({ name, fasName, fabName, farName, size, variant, ...rest }, ref) {
  const { generateTestId } = useTestId(rest, 'Icon');
  if (name) {
    const FeatherIconTag: React.FC<FeatherIcon.IconProps & { ref: ForwardRef }> = ExtendedFeatherIcons[name];
    return <FeatherIconTag {...rest} ref={ref} {...generateTestId()} />;
  }
  if (fasName) {
    const icon = FaSolidIcon[fasName];
    return <FontAwesomeIconWithRef icon={icon as IconProp} {...rest} ref={ref} {...generateTestId()} />;
  }
  if (fabName) {
    const icon = FaBrandIcon[fabName];
    return <FontAwesomeIconWithRef icon={icon as IconProp} {...rest} ref={ref} {...generateTestId()} />;
  }
  if (farName) {
    const icon = FaRegularIcon[farName];
    return <FontAwesomeIconWithRef icon={icon as IconProp} {...rest} ref={ref} {...generateTestId()} />;
  }

  return <svg {...generateTestId()} />;
});

const noCircleBorder = (name?: string): boolean => {
  return !!name && FeatherIconsWithoutCircle.hasOwnProperty(name);
};

export const Icon = styled(IconBase)<IIconProps>`
  ${({ size = Size.Medium }) => iconStyles[size]}
  ${({ onClick }) =>
    onClick &&
    css`
      cursor: pointer;
    `}
  text-align: center;
  vertical-align: middle;
  display: inline-block;
  color: ${({ variant, theme }) => (variant ? theme.colours.icon[variant].text : 'inherit')};
  box-sizing: content-box;
  border-radius: 50%;
  overflow: visible;
  ${({ name }) =>
    noCircleBorder(name) &&
    css`
      &:first-of-type circle {
        stroke-width: 0;
      }
    `}
`;
